import React, { useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { Box, Button } from '@mui/material'
import {
  GridActionsCellItem,
  GridColumns,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridRowsProp,
  GridToolbarContainer,
  MuiEvent
} from '@mui/x-data-grid'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import { v4 as uuidv4 } from 'uuid'

function EditToolbar(props: EditToolbarProps) {
  const { setRows, setRowModesModel } = props

  const handleClick = () => {
    const id = uuidv4()

    setRows((oldRows) => [
      ...oldRows,
      {
        id,
        iccid: '',
        matching_id: '',
        sm_dp_address: '',
        qr_code: '',
        isNew: true
      }
    ])

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'iccid' }
    }))
  }

  return (
    <GridToolbarContainer>
      <Button color='primary' startIcon={<AddIcon />} onClick={handleClick}>
        Add record
      </Button>
    </GridToolbarContainer>
  )
}

export default function CRUDDataGrid(props: ICRUDDataGrid) {
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})
  const { rows, setRows } = props

  const handleRowEditStart = (
    params: GridRowParams,
    event: MuiEvent<React.SyntheticEvent>
  ) => {
    event.defaultMuiPrevented = true
  }

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event
  ) => {
    event.defaultMuiPrevented = true
  }

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
  }

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows.filter((row) => row.id !== id))
  }

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    })

    const editedRow = rows.find((row) => row.id === id)
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id))
    }
  }

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false } as IESimRow
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)))
    return updatedRow
  }

  const COLUMNS: GridColumns = [
    {
      field: 'iccid',
      headerName: 'ICCID',
      width: 150,
      editable: true,
      cellClassName: (params) => {
        if (!params.value) {
          return 'batch-update-error'
        } else {
          return 'batch-update-ok'
        }
      }
    },
    {
      field: 'matching_id',
      headerName: 'Matching ID',
      width: 150,
      editable: true,
      cellClassName: (params) => {
        if (!params.value) {
          return 'batch-update-error'
        } else {
          return 'batch-update-ok'
        }
      }
    },
    {
      field: 'sm_dp_address',
      headerName: 'SM DP Address',
      width: 150,
      editable: true,
      cellClassName: (params) => {
        if (!params.value) {
          return 'batch-update-error'
        } else {
          return 'batch-update-ok'
        }
      }
    },
    {
      field: 'qr_code',
      headerName: 'QR Code',
      width: 150,
      editable: true,
      cellClassName: (params) => {
        if (!params.value) {
          return 'batch-update-error'
        } else {
          return 'batch-update-ok'
        }
      }
    },
    {
      field: 'esim_config_id',
      headerName: 'ESIM Config ID',
      width: 150,
      editable: true,
      cellClassName: (params) => {
        if (!params.value) {
          return 'batch-update-error'
        } else {
          return 'batch-update-ok'
        }
      }
    },
    {
      field: 'action',
      type: 'actions',
      headerName: 'Actions',
      getActions: (params) => {
        const { id } = params

        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={uuidv4()}
              icon={<SaveIcon />}
              label='Save'
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key={uuidv4()}
              icon={<CancelIcon />}
              label='Cancel'
              className='textPrimary'
              onClick={handleCancelClick(id)}
              color='inherit'
            />
          ]
        }

        return [
          <GridActionsCellItem
            key={uuidv4()}
            icon={<EditIcon />}
            label='Edit'
            className='textPrimary'
            onClick={handleEditClick(id)}
            color='inherit'
          />,
          <GridActionsCellItem
            key={uuidv4()}
            icon={<DeleteIcon />}
            label='Delete'
            onClick={handleDeleteClick(id)}
            color='inherit'
          />
        ]
      }
    }
  ]

  return (
    <Box
      sx={{
        height: 500,
        width: '100%',
        '& .actions': {
          color: 'text.secondary'
        },
        '& .textPrimary': {
          color: 'text.primary'
        },
        '& .batch-update-error': {
          backgroundColor: '#d47483'
        },
        '& .batch-update-ok': {
          backgroundColor: 'rgba(157, 255, 118, 0.49)'
        }
      }}
    >
      <MUIDataGrid
        pageSize={10}
        rows={rows}
        columns={COLUMNS}
        editMode='row'
        checkboxSelection
        isRowSelectable={(params: GridRowParams) => {
          const row: IESimRow = params.row

          return !!(
            row.iccid &&
            row.sm_dp_address &&
            row.qr_code &&
            row.matching_id
          )
        }}
        rowModesModel={rowModesModel}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        components={{
          Toolbar: EditToolbar
        }}
        componentsProps={{
          toolbar: { setRows, setRowModesModel }
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </Box>
  )
}

interface EditToolbarProps {
  setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel
  ) => void
}

interface ICRUDDataGrid {
  rows: IESimRow[]
  setRows: (rows: IESimRow[]) => void
}

interface IESimRow {
  id: string
  iccid: string
  matching_id: string
  sm_dp_address: string
  qr_code: string
  esim_config_id: number
  isNew: boolean
}
